import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { vitrinaPublicUrl } from '../../utils/vitrinaPublicUrl';

/**
 * Barra superior fija en la landing de marketing (junto al hero nativo).
 * Responsive: menú hamburguesa en móvil para enlaces y acceso a login.
 */
export const MarketingFixedNav: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const vitrinaUrl = vitrinaPublicUrl();

  return (
    <header
      className="fixed top-0 left-0 right-0 z-[100] border-b border-slate-200/80 bg-white/95 shadow-sm backdrop-blur-md"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
        <div className="flex min-h-14 items-center justify-between gap-2 py-2 sm:h-[70px] sm:py-0">
          <Link
            to="/"
            className="flex shrink-0 items-center gap-2 text-lg font-extrabold tracking-tight text-[#0B1F4A] sm:text-xl"
            onClick={() => setMobileOpen(false)}
          >
            <img src="/clientelo-isotipo.png" alt="" className="h-8 w-8 object-contain sm:h-8" width={32} height={32} />
            <span>clientelo</span>
          </Link>

          {/* Desktop: enlaces centrados */}
          <nav
            className="hidden flex-1 items-center justify-center gap-6 text-sm font-medium text-slate-600 md:flex lg:gap-8"
            aria-label="Principal"
          >
            <Link to="/funciones" className="transition-colors hover:text-[#1A56DB]">
              Funciones
            </Link>
            <Link to="/negocios" className="transition-colors hover:text-[#1A56DB]">
              Negocios
            </Link>
            <Link to="/referidos" className="transition-colors hover:text-[#1A56DB]">
              Referidos
            </Link>
            <a
              href={vitrinaUrl}
              className="transition-colors hover:text-[#1A56DB]"
              target={vitrinaUrl.startsWith('http') ? '_blank' : undefined}
              rel={vitrinaUrl.startsWith('http') ? 'noreferrer' : undefined}
            >
              Vitrina Virtual
            </a>
            <Link to={{ pathname: '/', hash: 'precios' }} className="transition-colors hover:text-[#1A56DB]">
              Precio
            </Link>
            <Link to="/soporte" className="transition-colors hover:text-[#1A56DB]">
              Soporte
            </Link>
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden shrink-0 items-center gap-2 sm:gap-3 md:flex">
            <Link
              to="/login"
              className="rounded-lg border border-[#1A56DB] px-3 py-2 text-sm font-semibold text-[#1A56DB] transition-colors hover:bg-blue-50"
            >
              Iniciar sesión
            </Link>
            <Link
              to="/registro"
              className="rounded-lg bg-[#1A56DB] px-3 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#1546b8] sm:px-4"
            >
              Crear mi cuenta
            </Link>
          </div>

          {/* Móvil: CTAs compactos + hamburguesa */}
          <div className="flex items-center gap-2 md:hidden">
            <Link
              to="/login"
              className="rounded-md border border-[#1A56DB] px-2.5 py-1.5 text-xs font-semibold text-[#1A56DB]"
              onClick={() => setMobileOpen(false)}
            >
              Entrar
            </Link>
            <Link
              to="/registro"
              className="rounded-md bg-[#1A56DB] px-2.5 py-1.5 text-xs font-semibold text-white"
              onClick={() => setMobileOpen(false)}
            >
              Registro
            </Link>
            <button
              type="button"
              className="rounded-md p-2 text-slate-700 hover:bg-slate-100"
              aria-expanded={mobileOpen}
              aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'}
              onClick={() => setMobileOpen((o) => !o)}
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <nav
            className="border-t border-slate-100 py-3 md:hidden"
            aria-label="Principal móvil"
          >
            <div className="flex flex-col gap-1">
              <Link
                to="/funciones"
                className="rounded-md px-3 py-2.5 text-sm font-medium text-slate-800 hover:bg-slate-50"
                onClick={() => setMobileOpen(false)}
              >
                Funciones
              </Link>
              <Link
                to="/negocios"
                className="rounded-md px-3 py-2.5 text-sm font-medium text-slate-800 hover:bg-slate-50"
                onClick={() => setMobileOpen(false)}
              >
                Negocios
              </Link>
              <Link
                to="/referidos"
                className="rounded-md px-3 py-2.5 text-sm font-medium text-slate-800 hover:bg-slate-50"
                onClick={() => setMobileOpen(false)}
              >
                Referidos
              </Link>
              <a
                href={vitrinaUrl}
                className="rounded-md px-3 py-2.5 text-sm font-medium text-slate-800 hover:bg-slate-50"
                onClick={() => setMobileOpen(false)}
                target={vitrinaUrl.startsWith('http') ? '_blank' : undefined}
                rel={vitrinaUrl.startsWith('http') ? 'noreferrer' : undefined}
              >
                Vitrina Virtual
              </a>
              <Link
                to={{ pathname: '/', hash: 'precios' }}
                className="rounded-md px-3 py-2.5 text-sm font-medium text-slate-800 hover:bg-slate-50"
                onClick={() => setMobileOpen(false)}
              >
                Precio
              </Link>
              <Link
                to="/soporte"
                className="rounded-md px-3 py-2.5 text-sm font-medium text-slate-800 hover:bg-slate-50"
                onClick={() => setMobileOpen(false)}
              >
                Soporte
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};
